/**
 * Web Session ID Management
 *
 * Generates and persists a unique session ID for webchat users.
 * This session ID is used to create/resolve a Contact in the backend,
 * enabling memory persistence across conversations.
 */

import { useConsentStore } from "@/stores/consentStore";

const STORAGE_KEY = "lexe_web_session_id";
const COOKIE_NAME = "lexe_web_session_id";

/** Reset consent store on logout — called from clearAllUserData */
function resetConsentOnLogout(): void {
  try {
    useConsentStore.getState().reset();
  } catch {
    // Store may not be initialized — safe to ignore
  }
}
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

/**
 * Get a cookie by name
 */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

/**
 * Set a cookie with a value and max age
 */
function setCookie(name: string, value: string, maxAge: number): void {
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

/**
 * Generate a UUID v4
 * Uses crypto.randomUUID if available, otherwise falls back to manual generation
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a persistent web session ID.
 *
 * The session ID is stored in both localStorage and cookies for resilience.
 * If found in either, it's synchronized to both.
 * If not found, a new UUID is generated and stored.
 *
 * @returns The web session ID (UUID v4)
 */
export function getOrCreateWebSessionId(): string {
  // Try localStorage first
  let sessionId = localStorage.getItem(STORAGE_KEY);

  // Fallback to cookie if not in localStorage
  if (!sessionId) {
    sessionId = getCookie(COOKIE_NAME);
  }

  // Generate new if not found
  if (!sessionId) {
    sessionId = generateUUID();
    console.log("[session] Generated new web session ID:", sessionId);
  }

  // Ensure both localStorage and cookie are in sync
  try {
    localStorage.setItem(STORAGE_KEY, sessionId);
  } catch {
    console.warn("[session] Failed to save to localStorage");
  }

  try {
    setCookie(COOKIE_NAME, sessionId, COOKIE_MAX_AGE);
  } catch {
    console.warn("[session] Failed to save to cookie");
  }

  return sessionId;
}

/**
 * Clear the web session ID (for logout/reset scenarios).
 *
 * Note: This does NOT delete the Contact in the backend,
 * it only clears the local session binding.
 */
export function clearWebSessionId(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore errors
  }

  try {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0`;
  } catch {
    // Ignore errors
  }

  console.log("[session] Web session ID cleared");
}

/**
 * Check if a web session ID exists
 */
export function hasWebSessionId(): boolean {
  return !!(localStorage.getItem(STORAGE_KEY) || getCookie(COOKIE_NAME));
}

/**
 * Clear all localStorage keys matching a prefix.
 * Used for aggressive cleanup on logout.
 *
 * @param prefix - The prefix to match (e.g., 'lexe-')
 * @param whitelist - Keys to preserve even if they match the prefix
 */
export function clearStoragePattern(
  prefix: string,
  whitelist: string[] = [],
): void {
  const keysToRemove: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix) && !whitelist.includes(key)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn(`[session] Failed to remove ${key}:`, e);
    }
  });

  if (keysToRemove.length > 0) {
    console.info(
      `[session] Cleared ${keysToRemove.length} keys matching '${prefix}'`,
    );
  }
}

/**
 * Clear all user-specific data from localStorage and sessionStorage.
 *
 * Uses prefix-based clearing to catch all leo-* keys, with a whitelist
 * for non-sensitive UI preferences that should persist across users.
 *
 * MUST be called on:
 * - Logout
 * - Session integrity violation (different user detected)
 * - Cross-tab logout sync
 */
export function clearAllUserData(): void {
  // Whitelist: UI preferences that are safe to persist across users
  // (theme preference, language, etc. - NOT user data)
  const whitelist = ["lexe-theme", "lexe-language", "lexe-remember-me"];

  // Clear all lexe-* keys except whitelist
  clearStoragePattern("lexe-", whitelist);

  // Also clear the web session cookie (forces new session on next login)
  clearWebSessionId();

  // Reset consent state so next login re-checks
  resetConsentOnLogout();

  // Clear sessionStorage completely
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn("[session] Failed to clear sessionStorage:", e);
  }

  console.info("[session] All user data cleared");
}

/**
 * Validate that current session matches stored data.
 * Returns false if mismatch detected (stale data from different user).
 *
 * Note: This is a safety check, but the primary defense is NOT persisting
 * user data in the first place (conversations should not be in localStorage).
 */
export function validateSessionIntegrity(
  currentContactId: string | null,
): boolean {
  if (!currentContactId) return false;

  try {
    const chatStorage = localStorage.getItem("lexe-chat-storage");
    if (!chatStorage) return true; // No stored data, OK

    const parsed = JSON.parse(chatStorage);
    const storedContactId = parsed?.state?.userContactId;

    if (storedContactId && storedContactId !== currentContactId) {
      console.warn(
        `[session] INTEGRITY VIOLATION: stored contactId (${storedContactId}) ` +
          `!= current contactId (${currentContactId})`,
      );
      return false;
    }

    return true;
  } catch (e) {
    console.warn("[session] Failed to validate session integrity:", e);
    return false;
  }
}

export default {
  getOrCreateWebSessionId,
  clearWebSessionId,
  hasWebSessionId,
  clearStoragePattern,
  clearAllUserData,
  validateSessionIntegrity,
};
